import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { formatResources } from 'components/mobileTrainingProfile/helpers/requests';
import { useInfinitePersonnelSubordinates } from 'components/mobileTrainingProfile/hooks/reactQuery';

import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

const moreBreakpoint = 3;

export default function ManagementTab({ lineManager, personnelId }) {
  const [loadMoreRef, loadMoreInView] = useInView();

  const [showAllSubordinates, setShowAllSubordinates] = useState(false);

  const { t: i18nTCommon } = useTranslation('common');

  const {
    data: personnelSubordinatesQuery,
    fetchNextPage: fetchNextPageOfPersonnelSubordinates,
    hasNextPage: hasNextPageOfPersonnelSubordinates,
    isError: isPersonnelSubordinatesQueryError,
    isFetching: isFetchingPersonnelSubordinates,
    isLoading: isPersonnelSubordinatesQueryLoading,
  } = useInfinitePersonnelSubordinates({
    personnelId,
    select: personnelSubordinatesQuerySelector,
  });

  const allSubordinates =
    isPersonnelSubordinatesQueryLoading || isPersonnelSubordinatesQueryError ?
      []
    : personnelSubordinatesQuery.subordinates.resources;
  const visibleSubordinates =
    showAllSubordinates ? allSubordinates : (
      allSubordinates.slice(0, moreBreakpoint)
    );

  if (
    loadMoreInView &&
    !isFetchingPersonnelSubordinates &&
    hasNextPageOfPersonnelSubordinates
  )
    fetchNextPageOfPersonnelSubordinates();

  return (
    <div className='tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-4 tw-py-6'>
      <div className='tw-mb-6'>
        <div className='tw-mb-2 tw-font-semibold'>
          {i18nTCommon('shared.lineManager')}
        </div>
        {lineManager ?
          <div className='tw-inline-block tw-break-words tw-rounded-[16px] tw-bg-grey-050 tw-px-4 tw-py-1.5'>
            {personDisplayName({ attributes: lineManager.attributes })}
          </div>
        : <div className='tw-text-grey-500'>{i18nTCommon('shared.notSet')}</div>
        }
      </div>
      <div>
        <div className='tw-mb-2 tw-font-semibold'>
          {i18nTCommon('shared.reports')}
        </div>
        {!isPersonnelSubordinatesQueryLoading &&
          (allSubordinates.length > 0 ?
            <>
              <ul className='tw-m-0 tw-list-none tw-p-0'>
                {visibleSubordinates.map((subordinate) => (
                  <li className='tw-mb-2 last:tw-mb-0' key={subordinate.id}>
                    <span className='tw-inline-block tw-break-words tw-rounded-[16px] tw-bg-grey-050 tw-px-4 tw-py-1.5'>
                      {personDisplayName({
                        attributes: subordinate.attributes,
                      })}
                    </span>
                  </li>
                ))}
                {showAllSubordinates &&
                  !isFetchingPersonnelSubordinates &&
                  hasNextPageOfPersonnelSubordinates && (
                    <li ref={loadMoreRef}>
                      {i18nTCommon('personnel.loadingSubordinates')}
                    </li>
                  )}
              </ul>
              {!showAllSubordinates &&
                allSubordinates.length > moreBreakpoint && (
                  <div className='tw-mt-4 tw-flex tw-items-center'>
                    <button
                      className='app-link tw-bg-transparent tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                      onClick={() => setShowAllSubordinates(true)}
                    >
                      {i18nTCommon('shared.showAllWithMoreCount', {
                        count:
                          personnelSubordinatesQuery.meta.totalCount -
                          moreBreakpoint,
                      })}
                    </button>
                  </div>
                )}
            </>
          : <div className='tw-text-grey-500'>
              {i18nTCommon('shared.notSet')}
            </div>)}
      </div>
    </div>
  );
}

ManagementTab.propTypes = {
  personnelId: PropTypes.string.isRequired,
  lineManager: resourceShape('lineManager'),
};

const personnelSubordinatesQuerySelector = (response) => {
  const data = response.pages.map((page) => page.data).flat();

  return {
    subordinates: formatResources({
      resources: data,
      options: { resources: true },
    }),
    meta: response.pages[response.pages.length - 1].meta,
  };
};
